import React, { useEffect, useState } from "react";
import axios from "axios";
import { hasPermission } from "../utils/permissionUtils";
import { Link } from "react-router-dom";
import StudentCountTable from "../Dashboard/TotalStudents";
import AttendancePieChart from "../Components/Charts/AttendancePieChart";
import FeePieChart from "../Components/Charts/FeePieChart";
import TeacherAttendancePieChart from "../Components/Charts/TeacherAttendancePieChart";
import TeachingStaffCard from "../Dashboard/TeachingStaffCount";
import NonTeachingStaffCard from "../Dashboard/NonTeachingStaffCount";

const Dashboard = () => {
  const sessionId = localStorage.getItem("currentSessionId");
  const [eventCount, setEventCount] = useState(null);
  const [attendanceData, setAttendanceData] = useState(null);
  const [feeData, setFeeData] = useState(null);
   const [school, setSchool] = useState(null);
  const [loadingSchool, setLoadingSchool] = useState(true);
  // 🟣 Events (TOTAL count)
axios
  .get(
    `${process.env.REACT_APP_API_BASE_URL}/api/events/total-count`,
    {
      params: { sessionId },
    }
  )
  .then(res => setEventCount(res.data.totalEvents))
  .catch(() => setEventCount(0));

   useEffect(() => {
    const fetchSchool = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/school/getSchool`
        );

        setSchool(res.data?.[0] || null);
      } catch (err) {
        console.error(err);
        setSchool(null);
      } finally {
        setLoadingSchool(false);
      }
    };

    fetchSchool();
  }, []);
  useEffect(() => {
    if (!sessionId) return;

    const todayISO = new Date().toISOString().split("T")[0];

    // 🔵 Attendance (same API as chart)
    axios
      .get(
        `${process.env.REACT_APP_API_BASE_URL}/api/attendance/stats`,
        { params: { session: sessionId, date: todayISO } }
      )
      .then(res =>
        setAttendanceData({ ...res.data, date: todayISO })
      )
      .catch(() =>
        setAttendanceData({
          Present: 0,
          Absent: 0,
          Late: 0,
          onLeave: 0,
          date: todayISO,
        })
      );

// 🟢 Fee (TODAY total collection)
axios
  .get(
    `${process.env.REACT_APP_API_BASE_URL}/api/fees/report/today-total-collection`,
    {
      params: { sessionId },
    }
  )
  .then(res =>
    setFeeData({ totalPaid: res.data.totalCollection })
  )
  .catch(() => setFeeData({ totalPaid: 0 }));

  }, [sessionId]);

  // ===== DERIVED VALUES =====
  const attendancePercent = attendanceData
    ? Math.round(
        (attendanceData.Present /
          (attendanceData.Present +
            attendanceData.Absent +
            attendanceData.Late +
            attendanceData.onLeave)) *
          100
      )
    : null;

  const feeCollected = feeData?.totalPaid ?? null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">

      {/* ===== HEADER ===== */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-8">
               {/* School Name + Logo */}
        <div className="flex items-center gap-4">
          {school?.logo && (
            <img
              src={school.logo}
              alt="School Logo"
              className="w-14 h-14 rounded-full object-cover border border-gray-200"
            />
          )}

          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {loadingSchool ? "Loading..." : school?.schoolName || "School Dashboard"}
            </h1>
            <p className="text-gray-600 mt-1">
              A quick view of school performance and operations.
            </p>
          </div>
        </div>

        <div className="bg-white border border-indigo-100 rounded-xl px-5 py-3 shadow-sm">
          <p className="text-xs text-gray-500 font-medium">
            Current Academic Session
          </p>
          <p className="text-lg font-bold text-indigo-600">
            {localStorage.getItem("currentSessionName") || "Session Not Selected"}
          </p>
        </div>
      </div>

      {/* ===== STUDENT COUNTS ===== */}
      <div className="mb-6">
        <StudentCountTable />
      </div>

      {/* ===== SUMMARY CARDS ===== */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        <TeachingStaffCard />
        <NonTeachingStaffCard />

        {/* Fee Collection */}
        <Link to="/fee-report" className="flex">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 min-h-[110px] w-full hover:shadow-md hover:scale-[1.02] transition cursor-pointer">
          <p className="text-sm text-gray-500 font-medium">Fee Collected Today</p>
          <h3 className="text-3xl font-bold text-green-600 mt-1">
            {feeCollected === null
              ? "..."
              : `₹ ${feeCollected.toLocaleString("en-IN")}`}
          </h3>
        </div>
</Link>
        {/* Attendance */}
        <Link to="/view-attendance" className="flex">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 min-h-[110px] w-full hover:shadow-md hover:scale-[1.02] transition cursor-pointer">
          <p className="text-sm text-gray-500 font-medium">Attendance Today</p>
          <h3 className="text-3xl font-bold text-blue-600 mt-1">
            {attendancePercent === null ? "..." : `${attendancePercent}%`}
          </h3>
        </div>
        </Link>
        <Link to="/add-events" className="flex">
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 min-h-[110px] w-full hover:shadow-md hover:scale-[1.02] transition cursor-pointer">
    <p className="text-sm text-gray-500 font-medium">
      Total Events
    </p>
    <h3 className="text-3xl font-bold text-purple-600 mt-1">
      {eventCount === null ? "..." : eventCount}
    </h3>
  </div>
</Link>

      </div>

      {/* ===== CHARTS ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
  {hasPermission("student_attendance_chart") && (
    <div className="bg-white p-4 rounded-xl shadow min-h-[360px]">
      <AttendancePieChart data={attendanceData} />
    </div>
  )}

  {hasPermission("fee_deposit_chart") && (
    <div className="bg-white p-4 rounded-xl shadow min-h-[360px]">
      <FeePieChart data={feeData} />
    </div>
  )}

  {hasPermission("teacher_attendance_chart") && (
    <div className="bg-white p-4 rounded-xl shadow min-h-[360px]">
      <TeacherAttendancePieChart />
    </div>
  )}
</div>

    </div>
  );
};

export default Dashboard;
